import { useCallback, useEffect, useState } from 'react';
import type { ProgressStep, SessionEvent } from '@localagent/sdk';
import { findToolEventSeqs } from './activity-feed';
import { isImageHandle, thumbnailFor, useChatImageScope, type ChatImageScope } from './chat-image-scope';
import { MediaThumbnail, type MediaThumbnailState } from './MediaThumbnail';

function parseJson(text: string | undefined): Record<string, unknown> | undefined {
  if (!text) return undefined;
  try {
    const value: unknown = JSON.parse(text);
    return value && typeof value === 'object' ? (value as Record<string, unknown>) : undefined;
  } catch {
    // no-fallbacks.md: a withheld preview is a truncated skeleton; resolvePreviews hydrates the full event and fails if that does not parse.
    return undefined;
  }
}

function handlesFrom(step: ProgressStep, args: Record<string, unknown> | undefined, result: Record<string, unknown> | undefined): string[] {
  if (step.toolName === 'diagram_render') {
    if (step.toolResultStatus !== 'success') return [];
    const handle = result?.versioned_handle;
    if (typeof handle !== 'string' || !isImageHandle(handle)) {
      throw new Error('diagram_render succeeded without a media:// versioned_handle');
    }
    return [handle];
  }
  const images = args?.images;
  if (!Array.isArray(images) || images.some((h) => typeof h !== 'string')) {
    throw new Error('image_read arguments carry no images array of handle strings');
  }
  return images as string[];
}

async function resolvePreviews(
  step: ProgressStep,
  events: readonly SessionEvent[],
  getEventData: (seq: number) => Promise<Record<string, unknown>>,
): Promise<{ args?: Record<string, unknown>; result?: Record<string, unknown> }> {
  let args = parseJson(step.toolArgsPreview);
  let result = parseJson(step.toolResultPreview);
  const needArgs = step.toolName === 'image_read' && !args;
  const needResult = step.toolName === 'diagram_render' && !result && step.toolResultStatus === 'success';
  if (needArgs || needResult) {
    const { startSeq, resultSeq } = findToolEventSeqs(events, step);
    if ((needArgs && startSeq == null) || (needResult && resultSeq == null)) {
      throw new Error(`the ${step.toolName} event is outside the loaded history; scroll to load it`);
    }
    if (needArgs && startSeq != null) {
      args = parseJson((await getEventData(startSeq)).args_preview as string | undefined);
      if (!args) throw new Error('image_read arguments are not JSON after hydration');
    }
    if (needResult && resultSeq != null) {
      result = parseJson((await getEventData(resultSeq)).result_preview as string | undefined);
      if (!result) throw new Error('diagram_render result is not JSON after hydration');
    }
  }
  return { args, result };
}

function Thumbnail({ handle, index }: { handle: string; index: number }) {
  const scope = useChatImageScope();
  const [loaded, setLoaded] = useState<{ scope: ChatImageScope; state: MediaThumbnailState } | null>(null);
  const state: MediaThumbnailState = loaded?.scope === scope ? loaded.state : { kind: 'idle' };
  const load = useCallback(() => {
    setLoaded({ scope, state: { kind: 'loading' } });
    void thumbnailFor(scope, handle).then((next) => setLoaded({ scope, state: next }));
  }, [scope, handle]);
  return (
    <MediaThumbnail
      state={state}
      label={handle}
      testId={`tool-image-thumbnail-${index}`}
      lightboxTestId="tool-image-lightbox"
      onVisible={load}
      onRetry={load}
    />
  );
}

export function ToolImageThumbnails({
  toolName,
  step,
  events,
  getEventData,
}: {
  toolName: string;
  step: ProgressStep;
  events: readonly SessionEvent[];
  getEventData: (seq: number) => Promise<Record<string, unknown>>;
}) {
  const [handles, setHandles] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const settled = step.toolResultStatus != null || !!step.toolResultError;

  useEffect(() => {
    if (toolName === 'diagram_render' && !settled) return;
    let cancelled = false;
    resolvePreviews(step, events, getEventData)
      .then(({ args, result }) => {
        if (cancelled) return;
        setHandles(handlesFrom(step, args, result));
        setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [step, events, getEventData, toolName, settled]);

  if (error) {
    return (
      <p data-testid="tool-image-thumbnails-error" className="text-caption text-error-text">
        Could not load images: {error}
      </p>
    );
  }
  if (!handles || handles.length === 0) return null;
  return (
    <div data-testid="tool-image-thumbnails" className="mt-1 flex flex-wrap gap-2 pl-1">
      {handles.map((handle, i) => (
        <Thumbnail key={`${i}:${handle}`} handle={handle} index={i} />
      ))}
    </div>
  );
}
